/*
 * VenueEditForm — inline edit form for admin/SimCell venue management.
 *
 * Replaces the detail panel content when the page is in edit mode.
 * For OSM venues it writes to ls_venue_overrides; for custom venues it
 * does a direct PATCH.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, FloppyDisk, Trash, Eye, EyeSlash, Star, MapPin, Bed,
  ArrowsClockwise, WarningCircle, CheckCircle, UploadSimple, Image,
} from '@phosphor-icons/react';

import logisticsApi from '../../lib/logisticsApi';

const AMENITY_OPTIONS = [
  'wifi', 'pool', 'gym', 'spa', 'restaurant', 'bar', 'parking',
  'room_service', 'concierge', 'business_center', 'laundry',
  'air_conditioning', 'airport_shuttle', 'pet_friendly',
  'ev_charging', 'meeting_rooms', 'rooftop_terrace', 'minibar',
  'breakfast', 'non_smoking', 'wheelchair', 'luggage_storage',
  '24h_front_desk', 'garden',
];

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
  { value: 'maintenance', label: 'Under Maintenance' },
  { value: 'fully_booked', label: 'Fully Booked' },
];

const SUBCATEGORY_OPTIONS = [
  { value: 'hotel', label: 'Hotel' },
  { value: 'hostel', label: 'Hostel' },
  { value: 'guest_house', label: 'Guest House' },
  { value: 'motel', label: 'Motel' },
  { value: 'apartment', label: 'Apartment' },
];


const VenueEditForm = ({
  open,
  onClose,
  venue,
  exerciseId,
  onSaved,
  isNew = false,
  initialLat,
  initialLng,
}) => {
  const isCustom = venue?.source === 'custom' || isNew;

  const [name, setName] = useState('');
  const [subcategory, setSubcategory] = useState('hotel');
  const [stars, setStars] = useState(3);
  const [priceDisplay, setPriceDisplay] = useState('95.00');
  const [status, setStatus] = useState('open');
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [hidden, setHidden] = useState(false);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [lat, setLat] = useState(0);
  const [lng, setLng] = useState(0);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);

  // Populate from venue data when opening
  useEffect(() => {
    if (!open) { setSuccess(false); setError(null); return; }
    if (isNew) {
      setName('');
      setSubcategory('hotel');
      setStars(3);
      setPriceDisplay('95.00');
      setStatus('open');
      setAddress('');
      setDescription('');
      setHidden(false);
      setSelectedAmenities([]);
      setLat(initialLat || 0);
      setLng(initialLng || 0);
      setUploadedImages([]);
      return;
    }
    if (!venue) return;
    setName(venue.name || '');
    setSubcategory(venue.subcategory || 'hotel');
    setStars(venue.stars || 3);
    setPriceDisplay(((venue.price_hint || 0) / 100).toFixed(2));
    setStatus(venue.status || 'open');
    setAddress(venue.address || '');
    setDescription(venue.description || '');
    setHidden(false);
    setSelectedAmenities(venue.amenities || venue.features || []);
    setLat(venue.lat || 0);
    setLng(venue.lng || 0);
    setUploadedImages(venue.images || []);
  }, [open, venue?.venue_id, isNew, initialLat, initialLng]);

  const toggleAmenity = (a) => {
    setSelectedAmenities((prev) =>
      prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]
    );
  };

  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !venue?.venue_id) return;
    setUploading(true);
    try {
      const result = await logisticsApi.uploadVenueImage(venue.venue_id, file);
      setUploadedImages((prev) => [...prev, result.url]);
    } catch (err) {
      setError(err.message || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  }, [venue?.venue_id]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const priceMinor = Math.round(parseFloat(priceDisplay || '0') * 100);
      if (isNew) {
        await logisticsApi.createVenue(exerciseId, {
          name, subcategory, stars, price_hint: priceMinor, status,
          address: address || null, description: description || null,
          amenities: selectedAmenities, lat, lng, features: selectedAmenities,
          images: uploadedImages.length > 0 ? uploadedImages : undefined,
        });
      } else if (isCustom) {
        await logisticsApi.updateVenue(venue.venue_id, {
          name, subcategory, stars, price_hint: priceMinor, status,
          address: address || null, description: description || null,
          amenities: selectedAmenities, features: selectedAmenities,
          images: uploadedImages.length > 0 ? uploadedImages : undefined,
        });
      } else {
        await logisticsApi.overrideVenue(exerciseId, venue.venue_id, {
          name, price_hint: priceMinor, stars, status,
          description: description || null,
          amenities: selectedAmenities,
          features: selectedAmenities,
          hidden,
          images: uploadedImages.length > 0 ? uploadedImages : undefined,
        });
      }
      setSuccess(true);
      onSaved?.();
      setTimeout(() => onClose?.(), 800);
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!venue?.venue_id || isNew) return;
    setDeleting(true);
    setError(null);
    try {
      if (isCustom) {
        await logisticsApi.deleteVenue(venue.venue_id);
      } else {
        await logisticsApi.overrideVenue(exerciseId, venue.venue_id, { hidden: true });
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="ls-glass fixed top-0 right-0 h-full z-50 flex flex-col w-full sm:w-[480px]"
            style={{ borderLeft: '1px solid var(--ls-border)' }}
            data-testid="ls-venue-edit-form"
            role="dialog"
            aria-label={isNew ? 'Add venue' : 'Edit venue'}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-5 h-14 shrink-0"
              style={{ borderBottom: '1px solid var(--ls-border)' }}
            >
              <div className="flex items-center gap-2">
                <Bed size={16} weight="fill" style={{ color: 'var(--ls-primary)' }} />
                <span className="ls-heading text-sm font-bold">
                  {isNew ? 'Add Venue' : `Edit · ${venue?.name || 'Venue'}`}
                </span>
              </div>
              <button onClick={onClose} className="ls-btn-ghost !p-2" aria-label="Close">
                <X size={14} weight="bold" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
              {success ? (
                <div className="text-center py-10">
                  <CheckCircle size={48} weight="duotone" style={{ color: 'var(--ls-success)' }} />
                  <p className="ls-heading text-lg font-bold mt-3">
                    {isNew ? 'Venue created' : 'Changes saved'}
                  </p>
                </div>
              ) : (
                <>
                  {/* Name */}
                  <label className="block">
                    <span className="ls-overline mb-1 block">Venue Name</span>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="ls-input w-full"
                      placeholder="e.g. Grand Hotel"
                      data-testid="ls-edit-name"
                    />
                  </label>

                  {/* Subcategory + Stars row */}
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="ls-overline mb-1 block">Type</span>
                      <select
                        value={subcategory}
                        onChange={(e) => setSubcategory(e.target.value)}
                        className="ls-input w-full"
                        data-testid="ls-edit-subcategory"
                      >
                        {SUBCATEGORY_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="ls-overline mb-1 block">Stars</span>
                      <div className="flex items-center gap-1 mt-1">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <button
                            key={s}
                            onClick={() => setStars(s)}
                            className="p-0.5"
                            data-testid={`ls-edit-star-${s}`}
                          >
                            <Star
                              size={20}
                              weight={s <= stars ? 'fill' : 'regular'}
                              style={{ color: s <= stars ? 'var(--ls-primary)' : 'var(--ls-muted)' }}
                            />
                          </button>
                        ))}
                      </div>
                    </label>
                  </div>

                  {/* Price + Status */}
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="ls-overline mb-1 block">Price / Night (Ÿ)</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={priceDisplay}
                        onChange={(e) => setPriceDisplay(e.target.value)}
                        className="ls-input w-full ls-mono"
                        data-testid="ls-edit-price"
                      />
                    </label>
                    <label className="block">
                      <span className="ls-overline mb-1 block">Status</span>
                      <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="ls-input w-full"
                        data-testid="ls-edit-status"
                      >
                        {STATUS_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </label>
                  </div>

                  {/* Address */}
                  <label className="block">
                    <span className="ls-overline mb-1 block">Address</span>
                    <input
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="ls-input w-full"
                      placeholder="123 Main Street, City"
                      data-testid="ls-edit-address"
                    />
                  </label>

                  {/* Lat/Lng for new venues */}
                  {isNew && (
                    <div className="grid grid-cols-2 gap-3">
                      <label className="block">
                        <span className="ls-overline mb-1 block">Latitude</span>
                        <input
                          type="number"
                          step="0.0001"
                          value={lat}
                          onChange={(e) => setLat(parseFloat(e.target.value) || 0)}
                          className="ls-input w-full ls-mono text-sm"
                          data-testid="ls-edit-lat"
                        />
                      </label>
                      <label className="block">
                        <span className="ls-overline mb-1 block">Longitude</span>
                        <input
                          type="number"
                          step="0.0001"
                          value={lng}
                          onChange={(e) => setLng(parseFloat(e.target.value) || 0)}
                          className="ls-input w-full ls-mono text-sm"
                          data-testid="ls-edit-lng"
                        />
                      </label>
                    </div>
                  )}

                  {/* Description */}
                  <label className="block">
                    <span className="ls-overline mb-1 block">Description</span>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      className="ls-input w-full resize-none"
                      placeholder="Describe the venue..."
                      data-testid="ls-edit-description"
                    />
                  </label>

                  {/* Images */}
                  <div>
                    <span className="ls-overline mb-2 block">Images</span>
                    <div className="flex gap-2 flex-wrap mb-2">
                      {uploadedImages.map((url, i) => (
                        <div
                          key={i}
                          className="w-16 h-16 rounded bg-cover bg-center relative group"
                          style={{ backgroundImage: `url(${url})`, border: '1px solid var(--ls-border)' }}
                        >
                          <button
                            className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ background: 'var(--ls-danger)', color: '#fff', fontSize: 8 }}
                            onClick={() => setUploadedImages((prev) => prev.filter((_, j) => j !== i))}
                          >
                            <X size={8} weight="bold" />
                          </button>
                        </div>
                      ))}
                      <label
                        className="w-16 h-16 rounded flex flex-col items-center justify-center cursor-pointer"
                        style={{ border: '2px dashed var(--ls-border)', color: 'var(--ls-muted)' }}
                      >
                        {uploading ? (
                          <ArrowsClockwise size={14} className="animate-spin" />
                        ) : (
                          <>
                            <UploadSimple size={14} weight="bold" />
                            <span className="text-[8px] mt-0.5">Upload</span>
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageUpload}
                          disabled={uploading || (!venue?.venue_id && !isNew)}
                        />
                      </label>
                    </div>
                  </div>

                  {/* Amenities */}
                  <div>
                    <span className="ls-overline mb-2 block">Amenities</span>
                    <div className="grid grid-cols-3 gap-1.5">
                      {AMENITY_OPTIONS.map((a) => (
                        <button
                          key={a}
                          onClick={() => toggleAmenity(a)}
                          className="text-[10px] px-2 py-1.5 rounded text-left capitalize transition-colors"
                          style={{
                            background: selectedAmenities.includes(a) ? 'var(--ls-primary)' : 'var(--ls-elevated)',
                            color: selectedAmenities.includes(a) ? 'var(--ls-primary-fg)' : 'var(--ls-muted)',
                          }}
                          data-testid={`ls-edit-amenity-${a}`}
                        >
                          {a.replace(/_/g, ' ')}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Hide toggle (OSM venues only) */}
                  {!isCustom && !isNew && (
                    <div className="flex items-center gap-3 py-2">
                      <button
                        onClick={() => setHidden(!hidden)}
                        className="flex items-center gap-2 text-xs"
                        style={{ color: hidden ? 'var(--ls-danger)' : 'var(--ls-muted)' }}
                        data-testid="ls-edit-hidden-toggle"
                      >
                        {hidden ? <EyeSlash size={14} weight="fill" /> : <Eye size={14} weight="fill" />}
                        {hidden ? 'Venue will be hidden from users' : 'Venue is visible'}
                      </button>
                    </div>
                  )}

                  {error && (
                    <p className="text-sm flex items-start gap-1.5" style={{ color: 'var(--ls-danger)' }}>
                      <WarningCircle size={14} weight="fill" className="shrink-0 mt-0.5" />
                      {error}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            {!success && (
              <div
                className="px-5 py-4 shrink-0 flex items-center gap-3"
                style={{ borderTop: '1px solid var(--ls-border)' }}
              >
                {!isNew && (
                  <button
                    className="ls-btn-ghost text-xs"
                    onClick={handleDelete}
                    disabled={deleting}
                    style={{ color: 'var(--ls-danger)' }}
                    data-testid="ls-edit-delete"
                  >
                    {deleting ? <ArrowsClockwise size={12} className="animate-spin" /> : <Trash size={12} weight="bold" />}
                    {isCustom ? 'Delete' : 'Hide'}
                  </button>
                )}
                <div className="flex-1" />
                <button className="ls-btn-ghost" onClick={onClose}>Cancel</button>
                <button
                  className="ls-btn-primary"
                  onClick={handleSave}
                  disabled={saving || !name.trim()}
                  data-testid="ls-edit-save"
                >
                  {saving ? (
                    <><ArrowsClockwise size={14} className="animate-spin" /> Saving…</>
                  ) : (
                    <><FloppyDisk size={14} weight="fill" /> {isNew ? 'Create' : 'Save'}</>
                  )}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default VenueEditForm;
